import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, fmtDateShort } from "../../lib/api";
import { PageHeader } from "../../lib/ui";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import { Scissors, Shirt, Loader2, PackageCheck, CheckCircle2, RefreshCw, User, CalendarClock, Search, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const COL_META = {
  cutting: { icon: Scissors, accent: "border-t-amber-500", chip: "bg-amber-50 text-amber-700" },
  garment: { icon: Shirt, accent: "border-t-blue-500", chip: "bg-blue-50 text-blue-700" },
  proses: { icon: Loader2, accent: "border-t-violet-500", chip: "bg-violet-50 text-violet-700" },
  penerimaan: { icon: PackageCheck, accent: "border-t-orange-500", chip: "bg-orange-50 text-orange-700" },
  selesai: { icon: CheckCircle2, accent: "border-t-emerald-500", chip: "bg-emerald-50 text-emerald-700" },
};

const nf = (n) => Number(n || 0).toLocaleString("id-ID");

function Card({ c, onClick }) {
  const isCut = c.type === "cutting";
  return (
    <div
      onClick={onClick}
      className={`bg-white rounded-lg border p-3 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all cursor-pointer ${c.late ? "border-red-400 ring-1 ring-red-200" : "border-taupe/40"}`}
      data-testid={`kanban-card-${c.id}`}
    >
      <div className="flex items-center justify-between gap-2">
        <span className="font-mono text-xs font-semibold text-cocoa">{c.number}</span>
        {c.late
          ? <Badge className="bg-red-100 text-red-700 border-0 text-[10px] px-1.5 py-0 flex items-center gap-1"><AlertTriangle className="w-3 h-3" />Terlambat</Badge>
          : <Badge variant="outline" className="border-taupe/50 text-wmuted text-[10px] px-1.5 py-0">{c.status}</Badge>}
      </div>
      <div className="text-sm font-medium text-cocoa mt-1 leading-tight">{c.product_name || "-"}</div>
      <div className="text-[11px] text-wmuted mt-0.5">{c.plan_number}{!isCut && c.cutting_number ? ` · ${c.cutting_number}` : ""}</div>
      {c._stageTitle && <span className={`inline-block mt-1 text-[9px] px-1.5 py-0.5 rounded-full font-medium ${c._stageChip}`} data-testid={`kanban-card-stage-${c.id}`}>{c._stageTitle}</span>}

      <div className="flex flex-wrap gap-1.5 mt-2 text-[11px]">
        {isCut ? (
          <>
            <span className="px-1.5 py-0.5 rounded bg-cocoa/5 text-cocoa">Target: <b>{nf(c.target)}</b></span>
            <span className="px-1.5 py-0.5 rounded bg-cocoa/5 text-cocoa">Hasil: <b>{nf(c.output)}</b></span>
            {c.sent > 0 && <span className="px-1.5 py-0.5 rounded bg-blue-50 text-blue-700">Terkirim: <b>{nf(c.sent)}</b></span>}
          </>
        ) : (
          <>
            <span className="px-1.5 py-0.5 rounded bg-cocoa/5 text-cocoa">Kirim: <b>{nf(c.send)}</b></span>
            <span className="px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700">Diterima: <b>{nf(c.received)}</b>/{nf(c.send)}</span>
          </>
        )}
      </div>

      <div className="flex items-center justify-between mt-2 text-[11px] text-wmuted">
        <span className="flex items-center gap-1 truncate">
          <User className="w-3 h-3" />{isCut ? (c.pic || "-") : (c.partner_name || c.pic || "-")}
        </span>
        {c.target_date && <span className={`flex items-center gap-1 ${c.late ? "text-red-600 font-medium" : ""}`}><CalendarClock className="w-3 h-3" />{fmtDateShort(c.target_date)}</span>}
      </div>
    </div>
  );
}

export default function ProductionKanban() {
  const navigate = useNavigate();
  const [columns, setColumns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [view, setView] = useState("all");

  const load = () => {
    setLoading(true);
    api.get("/production/kanban")
      .then((r) => setColumns(r.data.columns || []))
      .catch(() => toast.error("Gagal memuat papan"))
      .finally(() => setLoading(false));
  };
  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, []);

  const q = search.trim().toLowerCase();
  const match = (c) => !q || [c.number, c.product_name, c.plan_number, c.cutting_number, c.partner_name]
    .some((v) => (v || "").toLowerCase().includes(q));

  const goto = (c) => c.type === "cutting"
    ? navigate(`/production/cutting?focus=${c.id}`)
    : navigate(`/production/orders?detail=${c.id}`);

  const VENDOR_STAGE_KEYS = ["garment", "proses", "penerimaan"];
  const stageMeta = {
    garment: { t: "Garment", chip: COL_META.garment.chip },
    proses: { t: "Proses", chip: COL_META.proses.chip },
    penerimaan: { t: "Penerimaan", chip: COL_META.penerimaan.chip },
  };
  const vendorCols = React.useMemo(() => {
    const map = {};
    columns.filter((c) => VENDOR_STAGE_KEYS.includes(c.key)).forEach((col) => {
      (col.cards || []).forEach((c) => {
        const key = c.partner_name || "Tanpa Konveksi";
        (map[key] = map[key] || []).push({ ...c, _stageTitle: stageMeta[col.key].t, _stageChip: stageMeta[col.key].chip });
      });
    });
    return Object.entries(map).sort((a, b) => a[0].localeCompare(b[0])).map(([name, cards]) => ({ name, cards }));
  }, [columns]);

  return (
    <div data-testid="production-kanban-page">
      <PageHeader title="Board Production" subtitle="Pantau seluruh proses: Cutting → Garment → Proses → Penerimaan. Kartu berpindah otomatis mengikuti status.">
        <div className="flex items-center gap-2">
          <div className="flex rounded-lg border border-cocoa/30 overflow-hidden">
            <button onClick={() => setView("all")} className={`px-3 py-1.5 text-sm ${view === "all" ? "bg-cocoa text-ivory" : "bg-white text-cocoa"}`} data-testid="kanban-view-all">Semua Proses</button>
            <button onClick={() => setView("vendor")} className={`px-3 py-1.5 text-sm border-l border-cocoa/30 ${view === "vendor" ? "bg-cocoa text-ivory" : "bg-white text-cocoa"}`} data-testid="kanban-view-vendor">Per Konveksi</button>
          </div>
          <div className="relative">
            <Search className="w-4 h-4 text-wmuted absolute left-2.5 top-1/2 -translate-y-1/2" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Cari produk / plan / no…" className="pl-8 w-56 bg-white" data-testid="kanban-search" />
          </div>
          <Button variant="outline" onClick={load} disabled={loading} className="border-cocoa text-cocoa" data-testid="kanban-refresh">
            <RefreshCw className={`w-4 h-4 mr-1.5 ${loading ? "animate-spin" : ""}`} /> Muat Ulang
          </Button>
        </div>
      </PageHeader>

      {view === "all" && <div className="flex gap-4 overflow-x-auto pb-4" data-testid="kanban-board">
        {columns.map((col) => {
          const meta = COL_META[col.key] || COL_META.cutting;
          const Icon = meta.icon;
          const cards = col.cards.filter(match);
          const pcs = cards.reduce((s, c) => s + (Number(c.pcs) || 0), 0);
          return (
            <div key={col.key} className="flex-shrink-0 w-72" data-testid={`kanban-col-${col.key}`}>
              <div className={`bg-white rounded-t-lg border border-b-0 border-taupe/40 border-t-4 ${meta.accent} px-3 py-2.5`}>
                <div className="flex items-center justify-between">
                  <span className="flex items-center gap-2 font-heading text-sm text-cocoa font-semibold">
                    <Icon className="w-4 h-4" />{col.title}
                  </span>
                  <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${meta.chip}`} data-testid={`kanban-count-${col.key}`}>{cards.length}</span>
                </div>
                <div className="text-[11px] text-wmuted mt-1" data-testid={`kanban-pcs-${col.key}`}>Total {nf(pcs)} pcs</div>
              </div>
              <div className="bg-cocoa/[0.03] rounded-b-lg border border-taupe/40 p-2 space-y-2 min-h-[120px] max-h-[calc(100vh-260px)] overflow-y-auto">
                {cards.length === 0
                  ? <div className="text-center text-xs text-wmuted py-8">{q ? "Tidak ada yang cocok" : "Kosong"}</div>
                  : cards.map((c) => <Card key={c.id} c={c} onClick={() => goto(c)} />)}
              </div>
            </div>
          );
        })}
      </div>}

      {view === "vendor" && <div className="flex gap-4 overflow-x-auto pb-4" data-testid="kanban-vendor-board">
        {vendorCols.map((v) => {
          const cards = v.cards.filter(match);
          if (q && !cards.length) return null;
          const pcs = cards.reduce((s, c) => s + (Number(c.pcs) || 0), 0);
          return (
            <div key={v.name} className="flex-shrink-0 w-72" data-testid="kanban-vendor-col">
              <div className="bg-white rounded-t-lg border border-b-0 border-taupe/40 border-t-4 border-t-blue-500 px-3 py-2.5">
                <div className="flex items-center justify-between">
                  <span className="flex items-center gap-2 font-heading text-sm text-cocoa font-semibold truncate"><User className="w-4 h-4 flex-shrink-0" />{v.name}</span>
                  <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-blue-50 text-blue-700">{cards.length}</span>
                </div>
                <div className="text-[11px] text-wmuted mt-1">Total {nf(pcs)} pcs</div>
              </div>
              <div className="bg-cocoa/[0.03] rounded-b-lg border border-taupe/40 p-2 space-y-2 min-h-[120px] max-h-[calc(100vh-260px)] overflow-y-auto">
                {cards.length === 0
                  ? <div className="text-center text-xs text-wmuted py-8">{q ? "Tidak ada yang cocok" : "Kosong"}</div>
                  : cards.map((c) => <Card key={c.id} c={c} onClick={() => goto(c)} />)}
              </div>
            </div>
          );
        })}
        {!vendorCols.length && <div className="text-sm text-wmuted py-8">Belum ada order di konveksi (Garment / Proses / Penerimaan).</div>}
      </div>}
    </div>
  );
}
